/**
 * Phase 4 slice 3 — Record a concrete pour + cube set. Responsive (desktop
 * /quality/cube-tests/new + mobile /m/quality/cube-test). Site engineer captures
 * the pour; 7/14/28-day results are logged later on the detail page.
 */
import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Send, Loader2, FlaskConical } from 'lucide-react';
import PageHeader from '../../components/PageHeader';
import PhotoCaptureField from '../../components/safety/PhotoCaptureField';
import { apiGet, apiPost } from '../../lib/api';
import { toast } from 'sonner';

const GRADES = ['M15', 'M20', 'M25', 'M30', 'M35', 'M40', 'M45', 'M50', 'M60', 'M70', 'Other'];
const SUPPLIERS = ['In-House', 'RMC Vendor'];

const Label = ({ children }) => (
  <label className="block text-[10px] font-display font-bold uppercase tracking-widest text-slate-500 mb-1">{children}</label>
);

export default function CubeTestRecord() {
  const navigate = useNavigate();
  const [projects, setProjects] = useState([]);
  const [form, setForm] = useState({
    project_id: '', pour_date: new Date().toISOString().slice(0, 10), pour_location_on_site: '',
    concrete_grade: 'M25', concrete_grade_other: '', target_strength_n_mm2: '',
    mix_design_ref: '', batch_number: '', supplier: 'In-House', supplier_name: '',
    slump_mm: '', ambient_temp_c: '', cube_count: 3, notes: '',
  });
  const [photos, setPhotos] = useState([]);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    apiGet('/projects').then((p) => {
      setProjects(p);
      if (p.length) setForm((f) => ({ ...f, project_id: p[0].id }));
    }).catch(() => toast.error('Failed to load projects'));
  }, []);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async () => {
    if (!form.project_id) { toast.error('Select a project'); return; }
    setSaving(true);
    try {
      const payload = {
        ...form,
        cube_count: Number(form.cube_count || 3),
        slump_mm: form.slump_mm ? Number(form.slump_mm) : null,
        ambient_temp_c: form.ambient_temp_c ? Number(form.ambient_temp_c) : null,
        photos_base64: photos,
      };
      const doc = await apiPost('/quality/cube-tests', payload);
      toast.success('Cube test recorded');
      navigate(`/app/quality/cube-tests/${doc.id}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to save');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="max-w-2xl mx-auto px-4 sm:px-0 py-4 sm:py-0">
      <PageHeader overline="Quality" title="Record Cube Test" subtitle="Capture the pour and cube set — log 7/14/28-day strengths later" />

      <div className="space-y-4 bg-white sm:border sm:border-slate-200 rounded-sm sm:p-6" data-testid="cube-test-form">
        <div>
          <Label>Project</Label>
          <select value={form.project_id} onChange={set('project_id')} className="w-full px-3 py-2.5 border border-slate-300 rounded-sm text-sm bg-white" data-testid="ct-project-select">
            {projects.map((p) => <option key={p.id} value={p.id}>{p.code ? `${p.code} · ${p.name}` : p.name}</option>)}
          </select>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <div>
            <Label>Pour date</Label>
            <input type="date" value={form.pour_date} onChange={set('pour_date')} className="w-full px-3 py-2.5 border border-slate-300 rounded-sm text-sm" data-testid="ct-pour-date" />
          </div>
          <div>
            <Label>Pour location</Label>
            <input value={form.pour_location_on_site} onChange={set('pour_location_on_site')} placeholder="e.g. Block B, 3rd floor slab" className="w-full px-3 py-2.5 border border-slate-300 rounded-sm text-sm" data-testid="ct-location" />
          </div>
        </div>

        <div>
          <Label>Concrete grade</Label>
          <div className="grid grid-cols-4 sm:grid-cols-6 gap-2">
            {GRADES.map((g) => (
              <button key={g} type="button" onClick={() => setForm({ ...form, concrete_grade: g })}
                className={`px-2 py-2.5 rounded-sm text-xs font-bold border transition-colors ${form.concrete_grade === g ? 'bg-[#3b4872] text-white border-[#3b4872]' : 'bg-white text-slate-600 border-slate-300 hover:border-slate-400'}`}
                data-testid={`ct-grade-${g}`}>
                {g}
              </button>
            ))}
          </div>
        </div>

        {form.concrete_grade === 'Other' && (
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <Label>Grade label</Label>
              <input value={form.concrete_grade_other} onChange={set('concrete_grade_other')} placeholder="e.g. M55" className="w-full px-3 py-2.5 border border-slate-300 rounded-sm text-sm" data-testid="ct-grade-other" />
            </div>
            <div>
              <Label>Target strength (N/mm²)</Label>
              <input type="number" value={form.target_strength_n_mm2} onChange={set('target_strength_n_mm2')} placeholder="e.g. 55" className="w-full px-3 py-2.5 border border-slate-300 rounded-sm text-sm" data-testid="ct-target" />
            </div>
          </div>
        )}

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <div>
            <Label>Mix design ref</Label>
            <input value={form.mix_design_ref} onChange={set('mix_design_ref')} placeholder="MD-25-01" className="w-full px-3 py-2.5 border border-slate-300 rounded-sm text-sm" data-testid="ct-mix-ref" />
          </div>
          <div>
            <Label>Batch / RMC docket no.</Label>
            <input value={form.batch_number} onChange={set('batch_number')} className="w-full px-3 py-2.5 border border-slate-300 rounded-sm text-sm" data-testid="ct-batch" />
          </div>
        </div>

        <div>
          <Label>Supplier</Label>
          <div className="grid grid-cols-2 gap-2">
            {SUPPLIERS.map((s) => (
              <button key={s} type="button" onClick={() => setForm({ ...form, supplier: s })}
                className={`px-2 py-2.5 rounded-sm text-xs font-bold border transition-colors ${form.supplier === s ? 'bg-[#3b4872] text-white border-[#3b4872]' : 'bg-white text-slate-600 border-slate-300 hover:border-slate-400'}`}
                data-testid={`ct-supplier-${s.replace(/[^a-zA-Z]/g, '')}`}>
                {s}
              </button>
            ))}
          </div>
        </div>

        {form.supplier === 'RMC Vendor' && (
          <div>
            <Label>RMC vendor name</Label>
            <input value={form.supplier_name} onChange={set('supplier_name')} placeholder="e.g. ACC RMX, Pune" className="w-full px-3 py-2.5 border border-slate-300 rounded-sm text-sm" data-testid="ct-supplier-name" />
          </div>
        )}

        <div className="grid grid-cols-3 gap-3">
          <div>
            <Label>Slump (mm)</Label>
            <input type="number" value={form.slump_mm} onChange={set('slump_mm')} className="w-full px-3 py-2.5 border border-slate-300 rounded-sm text-sm" data-testid="ct-slump" />
          </div>
          <div>
            <Label>Ambient °C</Label>
            <input type="number" value={form.ambient_temp_c} onChange={set('ambient_temp_c')} className="w-full px-3 py-2.5 border border-slate-300 rounded-sm text-sm" data-testid="ct-temp" />
          </div>
          <div>
            <Label>No. of cubes</Label>
            <select value={form.cube_count} onChange={set('cube_count')} className="w-full px-3 py-2.5 border border-slate-300 rounded-sm text-sm bg-white" data-testid="ct-cube-count">
              {[3, 4, 5, 6].map((n) => <option key={n} value={n}>{n}</option>)}
            </select>
          </div>
        </div>

        <div>
          <Label>Notes</Label>
          <textarea value={form.notes} onChange={set('notes')} rows={2} className="w-full px-3 py-2.5 border border-slate-300 rounded-sm text-sm" data-testid="ct-notes" />
        </div>

        <div>
          <Label>Photos (pour / cubes being cast)</Label>
          <PhotoCaptureField photos={photos} setPhotos={setPhotos} />
        </div>

        <button onClick={submit} disabled={saving}
          className="w-full flex items-center justify-center gap-2 bg-[#3b4872] hover:bg-[#2d3856] text-white px-6 py-3.5 rounded-sm disabled:opacity-60 transition-colors font-display font-medium"
          data-testid="ct-submit-btn">
          {saving ? <Loader2 size={18} className="animate-spin" /> : <FlaskConical size={16} />}
          {saving ? 'Saving…' : 'Record Cube Test'}
        </button>
      </div>
    </div>
  );
}
